import React from 'react';
import { alpha, Box, ButtonBase, Stack, Typography } from '@mui/material';
import { CHECKOUT_COPY } from 'lib/billing';
import { useCheckoutActions, useCheckoutState } from '../store';
import { SECTION_LABEL_SX } from './sharedStyles';

// Trial-or-buy choice. Only rendered when the cart is trial-eligible and this
// is a fresh purchase (existing subscribers never trial). Picking "Buy now"
// sets `skipTrial`, which suppresses use_trial on checkout + the preview total.
export function TrialChoice() {
  const state = useCheckoutState();
  const actions = useCheckoutActions();

  // Shown whenever the cart is trial-*capable* (trial-enabled tiered product),
  // even when the account count exceeds the included bundle — in that case the
  // "Free trial" option is greyed rather than hidden, so it's clear why.
  //
  // `trialCapable` is now scoped to the plan's new-sub (`creates`) buckets: it's
  // true only when a brand-new trial-capable subscription would be created. So a
  // buyer with an existing Standard sub still sees the choice when adding a new
  // Futures product, but a pure qty bump on an owned product (owner-path modify,
  // which can't carry a trial) or an already-used trial keeps it hidden.
  const capable = Boolean((state as any).trialCapable);
  if (!capable) return null;

  const COPY = CHECKOUT_COPY.configure;
  const eligible = Boolean((state as any).trialEligible); // within included bundle
  const includedBase = Number((state as any).trialIncludedBase) || 0;
  const trialDays = Number((state as any).trialDays) || 0;
  const skipTrial = Boolean((state as any).skipTrial);

  // Over the included bundle → trial can't apply; force "Buy now" as the active
  // choice and disable the trial tile (with an explanatory note).
  const trialDisabled = !eligible;

  const options = [
    {
      skip: false,
      title: COPY.trialOptionTrialTitle(trialDays),
      note: trialDisabled ? COPY.trialOptionTrialLimit(includedBase) : COPY.trialOptionTrialNote,
      disabled: trialDisabled,
      selected: !trialDisabled && !skipTrial
    },
    {
      skip: true,
      title: COPY.trialOptionBuyTitle,
      note: COPY.trialOptionBuyNote,
      disabled: false,
      selected: skipTrial || trialDisabled
    }
  ];

  return (
    <Stack spacing={1}>
      <Typography variant="overline" sx={SECTION_LABEL_SX}>
        {COPY.trialChoiceLabel}
      </Typography>
      <Box role="radiogroup" aria-label={COPY.trialChoiceLabel} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        {options.map((opt) => {
          const { selected, disabled } = opt;
          return (
            <ButtonBase
              key={String(opt.skip)}
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => actions.setSkipTrial(opt.skip)}
              sx={(theme) => ({
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'left',
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'border-color 150ms ease, background 150ms ease',
                '&:hover': selected || disabled ? {} : { borderColor: 'primary.light' }
              })}
            >
              <Typography variant="body2" fontWeight={700} color={selected ? 'primary.main' : 'text.primary'}>
                {opt.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {opt.note}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Stack>
  );
}
